import { useEffect, useState } from 'react'
import type { Product } from '@mc/api'
import { getTiersAsync, tierMatchers } from '@mc/products'
import { useStaticData } from '@mc/static-data-react'

/**
 * Single source for the annual-pass (default tier) product used to derive
 * `product_id`/`contents` on the purchase-funnel events.
 *
 * Reads `annualPass` from the page's static data when present; otherwise falls
 * back to a client-side fetch of the current tier collection and picks the
 * default (lowest) tier's product. This guarantees a value on pages/apps that
 * don't provide `annualPass` server-side (e.g. the global nav on low-funnel
 * pages, or the BOGO app), while funnel pages that already have it skip the
 * fetch entirely.
 *
 * `getTiersAsync` caches its result at module level, so the fetch happens at
 * most once per session.
 */
export const useAnnualPass = (): Product | null | undefined => {
  const { annualPass } = useStaticData<{ annualPass?: Product | null }>()
  const [fallback, setFallback] = useState<Product | null>(null)

  useEffect(() => {
    // Funnel pages already provide annualPass — skip the fetch entirely.
    if (annualPass) return undefined

    let active = true
    getTiersAsync()
      .then((tiers) => {
        if (active) {
          setFallback(tiers.find(tierMatchers.first)?.product ?? null)
        }
      })
      // Non-critical analytics fallback — ignore fetch failures.
      .catch(() => {})

    return () => {
      active = false
    }
  }, [annualPass])

  return annualPass ?? fallback
}
